import React, { useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import { linksFor } from '@components/projectcard';
import { usePrefersReducedMotion, observeReveal } from '@hooks';

const StyledProjectsGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};

  a {
    position: relative;
    z-index: 1;
  }
`;

const StyledProject = styled.li`
  position: relative;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(12, 1fr);
  align-items: center;

  &:not(:last-of-type) {
    margin-bottom: 100px;
  }

  /* ── The alternating desktop layout ──────────────────────────────────────
     Confined to wide viewports on purpose. Below 768px the card is a single
     column, so side-swapping, right-aligned text and mirrored rows have
     nothing to mirror — and left in place they would fight the mobile block
     further down from a higher specificity. */
  @media (min-width: 769px) {
    &:nth-of-type(odd) {
      .project-content {
        grid-column: 7 / -1;
        text-align: right;
      }
      .project-tech-list {
        justify-content: flex-end;

        li {
          margin: 0 0 5px 20px;
        }
      }
      .project-links {
        justify-content: flex-end;
        margin-left: 0;
        margin-right: -10px;
      }
      .project-image {
        grid-column: 1 / 8;
      }
    }
  }

  @media (min-width: 769px) and (max-width: 1080px) {
    &:nth-of-type(odd) .project-content {
      grid-column: 5 / -1;
    }
  }

  .project-content {
    position: relative;
    grid-column: 1 / 7;
    grid-row: 1 / -1;
    /* Above .project-image (z-index 1). */
    z-index: 2;

    @media (min-width: 769px) and (max-width: 1080px) {
      grid-column: 1 / 9;
    }
  }

  .project-title {
    color: var(--text-emphasis);
    font-size: clamp(24px, 5vw, 28px);
    margin: 0 0 20px;
  }

  .project-description {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    z-index: 2;
    margin: 0;
    padding: 25px;
    border-radius: var(--border-radius);
    background-color: var(--surface);
    color: var(--text-secondary);
    font-size: var(--fz-lg);

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    strong {
      color: var(--text);
      font-weight: normal;
    }
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    z-index: 2;
    margin: 25px 0 10px;
    padding: 0;
    list-style: none;

    li {
      margin: 0 20px 5px 0;
      color: var(--text-secondary);
      font-family: var(--font-sans);
      font-size: var(--fz-xs);
      white-space: nowrap;
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 2;
    margin-top: 10px;
    margin-left: -10px;
    color: var(--text-emphasis);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 10px;
      transition: var(--transition);

      &:hover,
      &:focus-visible {
        color: var(--accent);
      }

      &.external svg {
        width: 22px;
        height: 22px;
        margin-top: -4px;
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .cta {
      ${({ theme }) => theme.mixins.smallButton};
      margin: 10px;
    }
  }

  .project-image {
    ${({ theme }) => theme.mixins.boxShadow};
    grid-column: 6 / -1;
    grid-row: 1 / -1;
    position: relative;
    z-index: 1;
    border-radius: var(--border-radius);
    overflow: hidden;

    a {
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      vertical-align: middle;

      &:hover,
      &:focus-visible {
        outline: 0;
      }
    }

    .img {
      border-radius: var(--border-radius);
    }
  }

  /* ── Narrow viewports ────────────────────────────────────────────────────
     The cover used to sit behind the copy as a dimmed, blurred backdrop, which
     made the text hard to read and looked nothing like the rest of the site.
     It is a card now, built to the same recipe as the project cards: cover on
     top at its own aspect ratio, then title, description, tech, links — same
     type sizes, same colours, same padding. */
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    ${({ theme }) => theme.mixins.boxShadow};
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    background-color: var(--surface-transparent);
    ${({ theme }) => theme.mixins.glass};
    overflow: hidden;

    /* Same affordance as the project cards: the border picks up the accent on
       hover, and :has(:focus-visible) rather than :focus-within so a mouse
       click does not leave it latched on. */
    &:hover,
    &:has(:focus-visible) {
      border-color: var(--accent);
    }

    &:hover .project-title,
    &:has(:focus-visible) .project-title {
      color: var(--accent);
    }

    &:not(:last-of-type) {
      margin-bottom: var(--space-5);
    }

    /* The cover is second in the DOM so it can sit beside the copy on desktop;
       here it belongs above it. */
    .project-image {
      order: -1;
      box-shadow: none;
      border-radius: 4px;
      width: 100% - 2.5rem;
      margin: 1.25rem 1.25rem 0;

      a,
      .img {
        border-radius: 0;
      }
    }

    .project-content {
      order: 0;
      padding: 1rem 1.25rem 1rem;
    }

    .project-title {
      margin: 0 0 var(--space-3);
      font-size: var(--fz-lg);
      font-weight: 500;

      /* Covers the card, so anywhere that is not one of the icon links opens
         the project — the same affordance the project cards use. */
      a {
        position: static;

        &:before {
          content: '';
          display: block;
          position: absolute;
          z-index: 0;
          inset: 0;
        }
      }
    }

    .project-description {
      padding: 0;
      background-color: transparent;
      box-shadow: none;
      color: var(--text-secondary);
      font-size: var(--fz-lg);

      &:hover {
        box-shadow: none;
      }
    }

    .project-tech-list {
      margin: var(--space-3) 0 0;

      li {
        margin: 0 var(--space-3) 4px 0;
        color: var(--text-muted);
        font-size: var(--fz-xxs);
      }
    }

    .project-links {
      margin-top: var(--space-3);
      margin-left: -7px;
      color: var(--text-secondary);

      a {
        padding: 5px 7px;

        &.external svg {
          width: 20px;
          height: 20px;
          margin-top: -2px;
        }

        svg {
          width: 18px;
          height: 18px;
        }
      }

      .cta {
        margin: 0 7px;
      }
    }
  }
`;

const Featured = () => {
  /* Reads from content/projects, same as every other card on the site. The
     three featured entries used to live in content/featured as near-duplicates
     of the project files; `featured: true` marks them now, so a project's
     details are written once and the two surfaces cannot disagree. */
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { featured: { eq: true } }
        }
        sort: { fields: [frontmatter___featuredOrder], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              slug
              description
              cover {
                childImageSharp {
                  gatsbyImageData(width: 700, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              instagram
              ios
              android
            }
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.map(({ node }) => node.frontmatter);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    // The title first, then each project a tenth of a second behind the last.
    const stop = [revealTitle.current, ...revealProjects.current].map((el, i) =>
      observeReveal(el, i * 100),
    );

    return () => stop.forEach(fn => fn && fn());
  }, [prefersReducedMotion]);

  return (
    <section id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        Some Things I’ve Built
      </h2>

      <StyledProjectsGrid>
        {featuredProjects.map((project, i) => {
          const { slug, title, description, tech, cover } = project;
          const image = getImage(cover);
          const links = linksFor(project);

          return (
            <StyledProject key={slug} ref={el => (revealProjects.current[i] = el)}>
              <div className="project-content">
                <div>
                  {/* The title and the cover both go to the project's own page.
                      They used to point at whichever external URL existed,
                      which meant the one card with a write-up behind it was the
                      one you could not reach. */}
                  <h3 className="project-title">
                    <Link to={slug}>{title}</Link>
                  </h3>

                  {description ? <p className="project-description">{description}</p> : null}

                  {tech?.length > 0 && (
                    <ul className="project-tech-list">
                      {tech.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}

                  {links.length > 0 && (
                    <div className="project-links">
                      {links.map(({ name, label, url, className }) => (
                        <a
                          key={name}
                          href={url}
                          className={className}
                          aria-label={`${title} on ${label}`}
                          target="_blank"
                          rel="noreferrer">
                          <Icon name={name} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {image && (
                <div className="project-image">
                  <Link to={slug} aria-label={title}>
                    <GatsbyImage image={image} alt={title} className="img" />
                  </Link>
                </div>
              )}
            </StyledProject>
          );
        })}
      </StyledProjectsGrid>
    </section>
  );
};

export default Featured;
